/**
 * The quest completion card (q-demo-dialogue-playtest-2026-10-29; AD-870 Amendment 1). Owner,
 * 2026-10-29: "Turning in a quest should probably trigger an audio line and some better visual
 * confirmation the quest was completed and any rewards received if applicable."
 *
 * Generic for every quest: raised by the server's `questTurnedIn` (the committed turn-in, never
 * the tap), it shows the quest's name, the NPC's `complete` line (spoken when it is recorded; the
 * line on the card is its subtitle either way) and what the turn-in GRANTED, with the level's XP
 * bar under it. It takes no tap (a tap on the world must reach the world, mobile-layout.md §3.1)
 * and sits above the play area's middle band; it leaves on its own after `CARD_MS`.
 */

import { useEffect } from 'react';

import { voiceChoice } from '../game/audio/index.js';
import { useGame } from './net-context.js';
import { nameOf } from './playerText.js';
import { completionCard, completionTalk, type RewardNames } from './panels/questDialogue.js';
import { Bar } from './widgets.js';

/** How long the card stays up (ms): long enough to read a line and a reward list. */
export const CARD_MS = 9_000;

const NAMES: RewardNames = {
  item: (id) => nameOf('items', id),
  faction: (id) => nameOf('factions', id),
};

export function QuestCompleteCard(): JSX.Element | null {
  const { state, store } = useGame();
  const done = state.questCompletion;
  const seq = done?.seq ?? 0;

  // Speak the NPC's `complete` line once per confirmed turn-in (AD-870: the turn-in's answer).
  useEffect(() => {
    if (!done) return undefined;
    voiceChoice(completionTalk(done), { kind: 'complete', questId: done.questId });
    const t = setTimeout(() => store.dismissCompletion(done.seq), CARD_MS);
    return () => clearTimeout(t);
    // One run per turn-in: `seq` names it; the payload never changes under the same seq.
  }, [seq]);

  if (!done) return null;
  const card = completionCard(done, NAMES);
  const level = state.level;
  return (
    <div
      className="hud-quest-complete"
      data-testid="quest-complete-card"
      role="status"
      data-hud-notice
    >
      <div className="hud-quest-complete-title">{card.title}</div>
      <div className="hud-quest-complete-name">{card.name}</div>
      {card.line ? (
        <div className="hud-quest-complete-line" data-testid="quest-complete-line">
          “{card.line}”
        </div>
      ) : null}
      {card.rewards.length > 0 ? (
        <ul className="hud-quest-complete-rewards" data-testid="quest-complete-rewards">
          {card.rewards.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      ) : null}
      {level && level.xpForNextLevel ? (
        <Bar
          value={level.xpIntoLevel}
          max={level.xpForNextLevel}
          height={6}
          color="var(--pxl-xp)"
          label={`Level ${level.level}`}
        />
      ) : null}
    </div>
  );
}
